import React, { type FC, useMemo, useRef, useState, useEffect } from 'react'
import Plot from 'react-plotly.js'
import type {
  ComponentThemeFactory,
  ComponentThemeProps,
  ConsumerRTHData,
  ConsumerRTHSeries,
} from '../../../themes/util/types'
import {
  useComponentStyle,
  useTheme,
  type ConsumerRTHState,
} from '../../../themes'

import { plotConsumerRTHData } from './helpers/consumerRTHPlotter'
import HoverBox from './HoverBox'

import type { ConsumerRTHGraphTheme } from '.'
import type { CSSProperties } from 'styled-components'

declare module '../../../themes/util/types' {
  interface ComponentThemes {
    ConsumerRTHGraph: ConsumerRTHGraphTheme
  }
}

export type ConsumerRTHGraphProps =
  ComponentThemeProps<ConsumerRTHGraphTheme> & {
    rthData?: ConsumerRTHData
  }

const baseConsumerRTHGraphTheme: ComponentThemeFactory<
  ConsumerRTHGraphTheme
> = ({ colors, fonts }) => ({
  Container: [
    {
      style: {
        position: 'relative',
        pointerEvents: 'none',
        paddingBottom: '1em',
      },
    },
  ],
  Body: [
    {
      style: {
        margin: '10px',
        padding: '10px',
        backgroundColor: String(colors.consumerRTH.chartBackground),
      },
    },
  ],
  BodyWithWatermark: [
    {
      style: {
        margin: '10px',
        padding: '10px',
        backgroundColor: String(colors.consumerRTH.paneBackground),
      },
    },
  ],
  Plot: [
    {
      style: {
        display: 'inline-block',
        pointerEvents: 'none',
        filter: 'inherit',
      },
    },
  ],
  PlotWithWatermark: [
    {
      style: {
        display: 'inline-block',
        pointerEvents: 'none',
        filter: 'blur(3px)',
      },
    },
  ],
  NoData: [
    {
      style: {
        position: 'absolute',
        top: '40%',
        left: '25%',
        opacity: '0.8',
        zIndex: '1',
        textAlign: 'center',
        lineHeight: '1.5',
      },
    },
  ],
  NoDataText: [
    {
      style: {
        fontFamily: String(fonts.defaultMedium),
        fontSize: '30px',
        color: String(colors.consumerRTH.primaryText),
      },
    },
  ],
  NoDataSubText: [
    {
      style: {
        fontFamily: String(fonts.defaultLight),
        fontSize: '20px',
        color: String(colors.consumerRTH.primaryText),
      },
    },
  ],
})

const defaultRTHData = {
  categories: [
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
  ],
  series: [
    {
      name: 'Aux Heat',
      data: [24, 24, 16.4, 24, 24, 24, 24, 24, 10],
      stack: 'heat',
      state: 'heat_aux' as ConsumerRTHState,
    },
    {
      name: 'Heat',
      data: [24, 24, 16.4, 24, 24, 24, 24, 24, 10],
      stack: 'heat',
      state: 'heat' as ConsumerRTHState,
    },
  ],
}

const ConsumerRTHGraph: FC<ConsumerRTHGraphProps> = ({
  styles,
  decoration,
  layout,
  rthData = defaultRTHData,
}) => {
  const [graphWidth, setGraphWidth] = useState(window.innerWidth * 0.5)

  const consumerRTHGraphStyles = useComponentStyle(
    'ConsumerRTHGraph',
    baseConsumerRTHGraphTheme,
    styles,
    layout,
    decoration,
  )

  const days = {
    Sunday: 'Sun',
    Monday: 'Mon',
    Tuesday: 'Tue',
    Wednesday: 'Wed',
    Thursday: 'Thu',
    Friday: 'Fri',
    Saturday: 'Sat',
  }

  const getShortDays = (rthData.categories as Array<keyof typeof days>).map(
    function (day) {
      return days[day]
    },
  )

  const filterData = useMemo(
    () =>
      rthData.series.filter(
        (series): series is ConsumerRTHSeries & { state: ConsumerRTHState } => {
          const reducedData = series.data.reduce(
            (accum, data) => (accum ?? 0) + (data ?? 0),
            0,
          )

          return (
            typeof series.state === 'string' &&
            (series.state.includes('heat') || series.state.includes('cool')) &&
            series.data.some((data) => data !== null && data !== 0) &&
            (reducedData ?? 0) > 0
          )
        },
      ),
    [rthData],
  )

  const { colors, fonts } = useTheme()
  const categories =
    rthData.categories.includes('Monday') ? getShortDays : rthData.categories
  const tickvals = useMemo(
    () => Array.from({ length: categories.length }).map((_, index) => index),
    [categories],
  )
  const plotData = useMemo(
    () => plotConsumerRTHData(filterData, colors),
    [filterData, colors],
  )
  const chartRef = useRef<HTMLDivElement>(null)
  const [isHovering, setIsHovering] = useState(false)
  const displayWaterMark = plotData.length === 0
  const [top, setTop] = useState(0)
  const [left, setLeft] = useState(0)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  const plotlyLayout: Partial<Plotly.Layout> = useMemo(
    () => ({
      width: graphWidth,
      barmode: 'stack',
      hovermode: 'closest',
      showlegend: true,
      yaxis: {
        title: 'Runtime (Hourly)',
      },
      xaxis: {
        ticktext: categories,
        tickvals,
      },
      barcornerradius: 5,
      plot_bgcolor:
        displayWaterMark ?
          String(colors.consumerRTH.border)
        : String(colors.consumerRTH.chartBackground),
      paper_bgcolor:
        displayWaterMark ?
          String(colors.consumerRTH.border)
        : String(colors.consumerRTH.chartBackground),
      font: {
        family: String(fonts.defaultLight),
        size: fonts.defaultSize,
        color: String(colors.consumerRTH.tableText),
      },
    }),
    [categories, tickvals, displayWaterMark, colors, fonts, graphWidth],
  )

  const config: Partial<Plotly.Config> = useMemo(
    () => ({
      displayModeBar: false,
    }),
    [],
  )

  useEffect(() => {
    const handleResize = () => {
      setGraphWidth(window.innerWidth * 0.5)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const onHover = (e: Readonly<Plotly.PlotHoverEvent>) => {
    e.event.preventDefault()
    if ('offsetY' in e.event && 'offsetX' in e.event) {
      setTop(e.event.offsetY as React.SetStateAction<number>)
      setLeft(e.event.offsetX as React.SetStateAction<number>)
    }
    e.points[0] && setSelectedIndex(e.points[0].pointIndex)
    setIsHovering(true)
  }

  const onUnhover = (e: Readonly<Plotly.PlotMouseEvent>) => {
    e.event.preventDefault()
    setIsHovering(false)
  }

  const bodyStyle =
    displayWaterMark ?
      consumerRTHGraphStyles.BodyWithWatermark
    : consumerRTHGraphStyles.Body
  const plotStyle =
    displayWaterMark ?
      consumerRTHGraphStyles.PlotWithWatermark
    : consumerRTHGraphStyles.Plot

  return (
    <div style={consumerRTHGraphStyles.Container as CSSProperties}>
      <div style={bodyStyle as CSSProperties}>
        <Plot
          style={plotStyle as CSSProperties}
          data={plotData}
          onHover={onHover}
          onUnhover={onUnhover}
          config={config}
          layout={plotlyLayout}
        />

        {displayWaterMark && (
          <div style={consumerRTHGraphStyles.NoData as CSSProperties}>
            <div style={consumerRTHGraphStyles.NoDataText as CSSProperties}>
              NO DATA
            </div>
            <div style={consumerRTHGraphStyles.NoDataSubText as CSSProperties}>
              There is no runtime data for this period
            </div>
          </div>
        )}

        {isHovering && (
          <HoverBox
            top={top}
            left={left}
            selectedIndex={selectedIndex}
            ref={chartRef}
            plotData={plotData}
          />
        )}
      </div>
    </div>
  )
}

export default ConsumerRTHGraph
